import clsx from 'clsx'
import React, {
  ButtonHTMLAttributes,
  createContext,
  HTMLAttributes,
  useCallback,
  useContext
} from 'react'

import { More } from '@/components-latest/primitives/Icons'
import OptionButton from '@/components-latest/primitives/OptionButton'
import {
  PopoverArrow,
  PopoverContent,
  PopoverTrigger
} from '@/components-latest/primitives/Popover'
import Popover from '@/components-latest/primitives/Popover/Popover'
import { useToggle } from '@/hooks/useToggle/useToggle'

import Styles from './MoreOptions.module.scss'

const MoreOptionsContext = createContext({
  toggleIsOpen: () => {
    return
  }
} as { toggleIsOpen: () => void })

const MoreOptions = React.memo(
  ({
    children,
    noArrow,
    align = 'end',
    popoverClassName,
    ...rest
  }: {
    children: React.ReactNode
    noArrow?: boolean
    align?: 'start' | 'end' | 'center'
    popoverClassName?: string
  } & HTMLAttributes<HTMLButtonElement>) => {
    const [isOpen, toggleIsOpen] = useToggle(false)

    return (
      <MoreOptionsContext.Provider value={{ toggleIsOpen }}>
        <Popover open={isOpen} onOpenChange={toggleIsOpen}>
          <PopoverTrigger {...rest}>
            <More />
          </PopoverTrigger>
          <PopoverContent
            align={align}
            className={clsx(Styles.popover, popoverClassName)}>
            {!noArrow && <PopoverArrow className={Styles.arrow} />}
            {children}
          </PopoverContent>
        </Popover>
      </MoreOptionsContext.Provider>
    )
  }
)
MoreOptions.displayName = 'MoreOptions'

const MoreOptionsItem = React.memo(
  ({
    children,
    icon,
    label,
    onClick,
    id,
    ...rest
  }: {
    icon?: React.ReactNode
    children?: React.ReactNode
    label?: string
    id: string
    onClick?: (id: string, e?: React.MouseEvent<HTMLButtonElement>) => void
  } & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'>) => {
    const { toggleIsOpen } = useContext(MoreOptionsContext)

    const onClickEnhanced = useCallback(
      (e: React.MouseEvent<HTMLButtonElement>) => {
        if (onClick) onClick(id, e)
        toggleIsOpen()
      },
      [toggleIsOpen, onClick, id]
    )

    return children ? (
      <OptionButton onClick={onClickEnhanced} icon={icon} {...rest}>
        {children}
      </OptionButton>
    ) : (
      <OptionButton
        onClick={onClickEnhanced}
        label={label}
        icon={icon}
        {...rest}
      />
    )
  }
)
MoreOptionsItem.displayName = 'MoreOptionsItem'

export default MoreOptions
export { MoreOptionsItem }
